import { match, P } from "ts-pattern";
import { formatCameraLabel } from "./key-parser";
import { getActiveRoute } from "./active-route";
import { isTenantSubpath, tenantPath } from "@src/features/tenants";

export interface WebBreadcrumbItem {
  label: string;
  href?: string;
}

interface WebBreadcrumbParams {
  pathname: string;
  tenantId?: string;
  siteId?: string;
  reportId?: string;
  cameraKey?: string;
  userId?: string;
  siteName?: string;
  action?: string;
}

export function getWebBreadcrumbs({
  pathname,
  tenantId,
  siteId,
  reportId,
  cameraKey,
  userId,
  siteName,
  action,
}: WebBreadcrumbParams): WebBreadcrumbItem[] {
  const route = getActiveRoute(pathname);

  return match(route)
    .with("cameras", () => getCameraBreadcrumbs({ pathname, tenantId, siteId, cameraKey, userId }))
    .with("settings", () => getSettingsBreadcrumbs({ pathname, userId }))
    .with("traffic-flow", () => getTrafficFlowBreadcrumbs({ tenantId, siteId, siteName }))
    .with("vehicles", () => getVehiclesBreadcrumbs({ pathname, tenantId, siteId }))
    .with("reports", () => getReportsBreadcrumbs({ pathname, tenantId, reportId }))
    .with("alarm-settings", () => getAlarmSettingsBreadcrumbs({ pathname, tenantId, siteName, action }))
    .with("audit-log", () => [{ label: "Audit Log" }])
    .with("clip-compilation", () => getClipCompilationBreadcrumbs({ pathname, tenantId }))
    .with("patriot-tools", () => getPatriotToolsBreadcrumbs({ pathname, tenantId }))
    .with(null, () => [{ label: "All cameras" }])
    .exhaustive();
}

function getClipCompilationBreadcrumbs({
  pathname,
  tenantId,
}: Pick<WebBreadcrumbParams, "pathname" | "tenantId">): WebBreadcrumbItem[] {
  const listHref = tenantId ? `/tenants/${tenantId}/clip-compilation` : undefined;

  if (pathname.endsWith("/clip-compilation")) {
    return [{ label: "Clip Compilations" }];
  }

  return [{ label: "Clip Compilations", href: listHref }, { label: "Player" }];
}

/** The two activation lists under Patriot tools, by URL segment and crumb label. */
const PATRIOT_ACTIVATION_LISTS = [
  { segment: "tenant-activations", label: "Tenant activations" },
  { segment: "fleet-activations", label: "Fleet activations" },
] as const;

function getPatriotToolsBreadcrumbs({
  pathname,
  tenantId,
}: Pick<WebBreadcrumbParams, "pathname" | "tenantId">): WebBreadcrumbItem[] {
  const rootHref = tenantPath(tenantId, "patriot-tools");

  for (const list of PATRIOT_ACTIVATION_LISTS) {
    const listPath = `patriot-tools/${list.segment}`;
    // Read from the pathname rather than a route param, which can lag a
    // navigation and would briefly label the crumb with the previous ticket.
    const ticketNo = pathname.match(new RegExp(`/${listPath}/([^/]+)`))?.[1];
    if (ticketNo !== undefined) {
      return [
        { label: "Patriot tools", href: rootHref },
        { label: list.label, href: tenantPath(tenantId, listPath) },
        { label: `Activation ${decodeURIComponent(ticketNo)}` },
      ];
    }
    if (isTenantSubpath(pathname, listPath)) {
      return [{ label: "Patriot tools", href: rootHref }, { label: list.label }];
    }
  }
  if (isTenantSubpath(pathname, "patriot-tools/tenant-signals")) {
    return [{ label: "Patriot tools", href: rootHref }, { label: "Tenant signals" }];
  }
  return [{ label: "Patriot tools" }];
}

function getCameraBreadcrumbs({
  pathname,
  tenantId,
  siteId,
  cameraKey,
  userId,
}: WebBreadcrumbParams): WebBreadcrumbItem[] {
  const cameraHref =
    tenantId && siteId && cameraKey ? `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}` : undefined;
  const alarmSettingsHref =
    tenantId && siteId && cameraKey ? `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarm` : undefined;
  const manageAccessHref =
    tenantId && siteId && cameraKey ?
      `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/manage-access`
    : undefined;

  if (pathname === "/" || pathname === "/overview" || pathname.includes("cameras-list")) {
    return [{ label: "All cameras" }];
  }

  if (siteId && !cameraKey) {
    return [{ label: "All sites", href: "/overview" }, { label: `Site ${siteId}` }];
  }

  if (!cameraKey) {
    return [{ label: "All cameras" }];
  }

  const cameraLabel = formatCameraLabel(decodeURIComponent(cameraKey));

  return match(pathname)
    .with(P.string.endsWith("/manage-access"), () => [
      { label: "All cameras", href: "/overview" },
      { label: cameraLabel, href: cameraHref },
      { label: "Manage Access" },
    ])
    .with(P.string.endsWith("/add-users-to-camera"), () => [
      { label: "All cameras", href: "/overview" },
      { label: cameraLabel, href: cameraHref },
      { label: "Manage Access", href: manageAccessHref },
      { label: "Add Users to Camera" },
    ])
    .with(P.string.endsWith("/scene-compare"), () => [
      { label: "All cameras", href: "/overview" },
      { label: cameraLabel, href: cameraHref },
      { label: "Scene Compare" },
    ])
    .with(P.string.endsWith("/dumping-search"), () => [
      { label: "All cameras", href: "/overview" },
      { label: cameraLabel, href: cameraHref },
      // Dumping Search is always reached from Scene Compare (the sibling route
      // that sets the search window), so link back to it explicitly. No search
      // state is carried — the crumb just reopens Scene Compare for this camera.
      { label: "Scene Compare", href: cameraHref ? `${cameraHref}/scene-compare` : undefined },
      { label: "Dumping Search" },
    ])
    .with(P.string.endsWith("/clips"), () => [
      { label: "All cameras", href: "/overview" },
      { label: cameraLabel, href: cameraHref },
      { label: "Recordings" },
    ])
    .with(P.string.endsWith("/activity-notification"), () => [
      { label: "All cameras", href: "/overview" },
      { label: cameraLabel, href: cameraHref },
      { label: "Notification settings" },
    ])
    .with(P.string.endsWith("/test-signals"), () => [
      { label: "All cameras", href: "/overview" },
      { label: cameraLabel, href: cameraHref },
      { label: "Test signals" },
    ])
    .with(P.string.includes("/alarms/"), () => {
      const pageLabel = match(pathname)
        .with(P.string.includes("/alarms/routine"), () => "Change Routine")
        .with(P.string.includes("/alarms/customize-routine"), () => "Customize Routine")
        .with(P.string.includes("/alarms/response"), () => "Change Response")
        .with(P.string.includes("/alarms/timezone"), () => "Timezone")
        .with(P.string.includes("/alarms/applied-schedule"), () => "Applied Schedule")
        .with(P.string.includes("/alarms/exceptions/"), () =>
          pathname.endsWith("/new") || pathname.endsWith("/create") ? "Add Exception" : "Edit Exception",
        )
        .otherwise(() => "Alarm Settings");

      return [
        { label: "All cameras", href: "/overview" },
        { label: cameraLabel, href: cameraHref },
        { label: "Alarm Settings", href: alarmSettingsHref },
        { label: pageLabel },
      ];
    })
    .with(P.string.includes("/alarm"), () => [
      { label: "All cameras", href: "/overview" },
      { label: cameraLabel, href: cameraHref },
      { label: "Alarm Settings" },
    ])
    .otherwise(() => [{ label: "All cameras", href: "/overview" }, { label: cameraLabel }]);
}

function getSettingsBreadcrumbs({
  pathname,
  userId,
}: Pick<WebBreadcrumbParams, "pathname" | "userId">): WebBreadcrumbItem[] {
  if (pathname === "/settings") {
    return [{ label: "Settings" }];
  }

  if (pathname === "/settings/users-and-permissions") {
    return [{ label: "Settings", href: "/settings" }, { label: "Users and Permissions" }];
  }

  if (pathname === "/settings/notification-preferences") {
    return [{ label: "Settings", href: "/settings" }, { label: "Notification Preferences" }];
  }

  if (pathname === "/settings/site-access/invite-user") {
    return [
      { label: "Settings", href: "/settings" },
      { label: "Users and Permissions", href: "/settings/users-and-permissions" },
      { label: "Invite User" },
    ];
  }

  if (pathname.startsWith("/settings/site-access/edit-user/")) {
    return [
      { label: "Settings", href: "/settings" },
      { label: "Users and Permissions", href: "/settings/users-and-permissions" },
      { label: "Edit User" },
    ];
  }

  if (pathname === "/settings/site-access/camera-access/assign-sites") {
    return [
      { label: "Settings", href: "/settings" },
      { label: "Users and Permissions", href: "/settings/users-and-permissions" },
      { label: "User profile", href: userId ? `/settings/site-access/camera-access/${userId}` : undefined },
      { label: "Assign sites" },
    ];
  }

  if (pathname.startsWith("/settings/site-access/camera-access/")) {
    return [
      { label: "Settings", href: "/settings" },
      { label: "Users and Permissions", href: "/settings/users-and-permissions" },
      { label: "User profile" },
    ];
  }

  return [{ label: "Settings" }];
}

function getVehiclesBreadcrumbs({
  pathname,
  tenantId,
  siteId,
}: Pick<WebBreadcrumbParams, "pathname" | "tenantId" | "siteId">): WebBreadcrumbItem[] {
  const allVehiclesHref = tenantId ? `/tenants/${tenantId}/vehicles` : "/vehicles";
  if (pathname.includes("/sightings")) {
    return [{ label: "Vehicle Search", href: allVehiclesHref }, { label: "Sightings" }];
  }
  if (siteId) {
    return [{ label: "Vehicle Search", href: allVehiclesHref }, { label: `Site ${decodeURIComponent(siteId)}` }];
  }
  return [{ label: "Vehicle Search" }];
}

function getTrafficFlowBreadcrumbs({
  tenantId,
  siteId,
  siteName,
}: Pick<WebBreadcrumbParams, "tenantId" | "siteId" | "siteName">): WebBreadcrumbItem[] {
  // The worklist is tenant-scoped (`/tenants/<id>/traffic-flow`). Fall back to
  // the un-scoped path only if the tenant is somehow unknown, which shouldn't
  // happen on this route but keeps the crumb clickable regardless.
  const worklistHref = tenantId ? `/tenants/${tenantId}/traffic-flow` : "/traffic-flow";

  // On the configure page a `site` is always present in the URL. Show the
  // resolved site name when available, otherwise the id (name comes from a
  // query that may not have resolved yet — see Breadcrumbs.web.tsx).
  if (siteId) {
    return [{ label: "Crossline Mapping", href: worklistHref }, { label: siteName || decodeURIComponent(siteId) }];
  }

  return [{ label: "Crossline Mapping" }];
}

/**
 * Pull the `<reportId>` out of a RoadSense report pathname
 * (`…/reports/roadsense/report/<reportId>[/site/<siteId>]`), or `undefined`
 * when there's no id segment (the bare `…/report`, an invalid URL). The
 * pathname is the crumb's source of truth, so this stays correct even when the
 * router's `reportId` param momentarily lags a navigation.
 */
function roadSenseReportIdFromPath(pathname: string): string | undefined {
  const segments = pathname.match(/\/reports\/roadsense\/report\/([^/]+)/);
  return segments?.[1];
}

function getReportsBreadcrumbs({
  pathname,
  tenantId,
  reportId,
}: Pick<WebBreadcrumbParams, "pathname" | "tenantId" | "reportId">): WebBreadcrumbItem[] {
  const reportsHref = tenantPath(tenantId, "reports");
  const wasteSenseHref = tenantPath(tenantId, "reports/wastesense");
  const roadSenseHref = tenantPath(tenantId, "reports/roadsense");

  // RoadSense report routes are lifecycle-first:
  // `…/reports/roadsense/report/<reportId>[/site/<siteId>]`. The report and site
  // names live in the report row's listingSummary, which isn't available
  // synchronously here, so the crumbs use generic labels rather than the raw
  // ULID/site id (which would be noise to a user). Matched ahead of the flat
  // `endsWith` cases below.
  if (isTenantSubpath(pathname, "reports/roadsense/report")) {
    // Prefer the report id parsed from the pathname (the source of truth for the
    // crumb) over the router param. During an expo-router navigation the param
    // can briefly lag the pathname, and a missing `reportId` here used to build a
    // trailing `…/report/` href that resolves to an Unmatched Route — the
    // intermittent broken "Report" link. Parsing the segment keeps the href in
    // lockstep with the URL the user is actually on.
    const pathReportId = roadSenseReportIdFromPath(pathname) ?? reportId;
    // Only link the "Report" crumb when we actually have an id to point at;
    // otherwise it's a plain (non-link) crumb rather than a link to a 404.
    const reportHref = pathReportId ? tenantPath(tenantId, `reports/roadsense/report/${pathReportId}`) : undefined;
    const crumbs: WebBreadcrumbItem[] = [
      { label: "Reports", href: reportsHref },
      { label: "RoadSense", href: roadSenseHref },
    ];
    // Only the site-detail route has a `/site/<siteId>` segment; keying off the
    // pathname (rather than the `siteId` param, which can linger from a prior
    // detail visit) avoids a spurious crumb on the site *list*.
    if (pathname.includes("/site/")) {
      crumbs.push({ label: "Report", href: reportHref }, { label: "Site Detail" });
    } else {
      crumbs.push({ label: "Report" });
    }
    return crumbs;
  }

  return match(pathname)
    .with(P.string.endsWith("/reports"), () => [{ label: "Reports" }])
    .with(P.string.endsWith("/reports/wastesense"), () => [
      { label: "Reports", href: reportsHref },
      { label: "WasteSense" },
    ])
    .with(P.string.endsWith("/reports/wastesense/report"), () => [
      { label: "Reports", href: reportsHref },
      { label: "WasteSense", href: wasteSenseHref },
      { label: "Report" },
    ])
    .with(P.string.endsWith("/reports/roadsense"), () => [
      { label: "Reports", href: reportsHref },
      { label: "RoadSense" },
    ])
    .otherwise(() => [{ label: "Reports", href: reportsHref }]);
}

function getAlarmSettingsBreadcrumbs({
  pathname,
  tenantId,
  siteName,
  action,
}: Pick<WebBreadcrumbParams, "pathname" | "tenantId" | "siteName" | "action">): WebBreadcrumbItem[] {
  const siteAlarmSettingsHref = tenantId ? `/tenants/${tenantId}/alarm-settings` : "/alarm-settings";

  if (pathname.endsWith("/alarm-settings")) {
    return [{ label: "Controls" }];
  }

  const siteIdMatch = pathname.match(/\/alarm-settings\/([^/]+)/);
  const siteId = siteIdMatch?.[1];
  const siteSettingsHref = siteId ? `${siteAlarmSettingsHref}/${siteId}` : siteAlarmSettingsHref;
  const manageRoutinesHref = siteId ? `${siteSettingsHref}/manage-routines` : siteAlarmSettingsHref;
  const siteLabel = siteName ?? siteId ?? "Site";

  if (
    !pathname.includes("/manage-") &&
    !pathname.includes("/edit-") &&
    !pathname.includes("/create-") &&
    !pathname.includes("/change-") &&
    !pathname.includes("/override-site") &&
    siteId
  ) {
    return [{ label: "Controls", href: siteAlarmSettingsHref }, { label: siteLabel }];
  }

  const pageLabel = match(pathname)
    .with(P.string.includes("/manage-routines"), () => "Manage routines")
    .with(P.string.includes("/edit-routine"), () => "Edit routine")
    .with(P.string.includes("/change-routine"), () => "Change routine")
    .with(P.string.includes("/manage-exceptions"), () => "Manage exceptions")
    .with(P.string.includes("/create-exception"), () => "Create exception")
    .with(P.string.includes("/edit-exception"), () => "Edit exception")
    .with(P.string.includes("/manage-alarm-response"), () => "Alarm response")
    .with(P.string.includes("/override-site"), () => "Override site")
    .otherwise(() => "");

  if (pathname.includes("/edit-routine") || pathname.includes("/change-routine")) {
    return [
      { label: "Controls", href: siteAlarmSettingsHref },
      { label: siteLabel, href: siteSettingsHref },
      { label: "Manage routines", href: manageRoutinesHref },
      { label: pageLabel },
    ];
  }

  // Override site with action param shows an extra breadcrumb level
  if (pathname.includes("/override-site")) {
    const overrideSiteHref = `${siteSettingsHref}/override-site`;
    if (action === "arm" || action === "disarm") {
      const actionLabel = action === "arm" ? "Arm site" : "Disarm site";
      return [
        { label: "Controls", href: siteAlarmSettingsHref },
        { label: siteLabel, href: siteSettingsHref },
        { label: "Override site", href: overrideSiteHref },
        { label: actionLabel },
      ];
    }
    return [
      { label: "Controls", href: siteAlarmSettingsHref },
      { label: siteLabel, href: siteSettingsHref },
      { label: pageLabel },
    ];
  }

  return [
    { label: "Controls", href: siteAlarmSettingsHref },
    { label: siteLabel, href: siteSettingsHref },
    { label: pageLabel },
  ];
}
